'use client';

import { useEffect, useState } from 'react';

import { useAuthToken } from '@ps/functions/auth/useAuthToken';
import {
  PopStoreExtendedSubscriptionLevel,
  type PopStoreSubscriptionLevel,
} from '@ps/models';

import { checkoutUrlBuilder } from '../../checkout/builders';
import {
  isActiveSubscription,
  isAvailableSubscription,
  subscriptionsToList,
} from '../../subscription/configuration/functions/access-control';
import {
  compareExtendedSubscriptionLevel,
  getSubscriptionDisplayName,
} from '../../subscription/configuration/functions/subscription-display';
import { useFetchAllSubscriptions } from '../../subscription/configuration/hooks/useFetchAllSubscriptions';
import type { CreatorSubscriptionPlan } from '../types/CreatorSubscriptionPlan';
import type { SelectedPlan } from '../types/SelectedPlan';
import { useUserCurrentSubscriptionOfCreator } from './useUserCurrentSubscriptionOfCreator';

interface UseSubscribePickerArgs {
  creatorId: string;
  /** Creator display name + follower-tier config, from the creator profile. */
  storeFrontName: string;
  followersSettings?: { active?: boolean; description?: string };
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  minLevel?: PopStoreExtendedSubscriptionLevel;
  /** Whether the viewer follows the creator (reactive; `undefined` while the
   *  follow status is still resolving). Feeds the default-plan pick — RN treats
   *  a follow as the FOLLOWER "current level" so the default lands on the next
   *  tier up, never back on Followers. The default-select waits for this to
   *  resolve so it never transiently picks Followers before the follow is known. */
  isFollowing?: boolean;
}

/**
 * Drives the subscribe tier picker (RN `index` page): builds the ordered plan
 * list from the creator's active subscriptions + the free Followers tier,
 * tracks the current selection, and computes the default preselection. Ports
 * the index-page parts of PopshopRN's `useSubscribeFlowController`.
 *
 * The paid payment leg is NOT here — it's the existing
 * `community_membership_subscription` checkout intent. {@link buildCheckoutUrl}
 * produces the `/checkout?…` URL to hand a paid tier off to.
 */
export function useSubscribePicker({
  creatorId,
  storeFrontName,
  followersSettings,
  defaultLevel = PopStoreExtendedSubscriptionLevel.FAN,
  minLevel,
  isFollowing,
}: UseSubscribePickerArgs) {
  const {
    subscriptions,
    refresh,
    loading: isLoadingCreatorSubscriptions,
  } = useFetchAllSubscriptions(creatorId);
  const {
    isLoading: isLoadingUserCurrentSubscription,
    currentSubscriptionInfo,
  } = useUserCurrentSubscriptionOfCreator(creatorId);

  // `fetchAllSubscriptions` needs a Firebase ID token. On a hard refresh the
  // hook's first fetch fires before the session restores (`auth().currentUser`
  // is still null → the fetch throws "User is not authenticated" and the tier
  // list stays empty, showing only the tokenless Follower row). Re-run the
  // fetch once the token resolves so the paid tiers appear. `useAuthToken`
  // fires on restore + on silent refresh.
  const [authToken] = useAuthToken();
  useEffect(() => {
    if (authToken && subscriptions == null) void refresh();
  }, [authToken, subscriptions, refresh]);

  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan>();

  // React Compiler memoizes these derivations; no manual useMemo needed.
  const activeSubscriptions = !subscriptions
    ? []
    : subscriptionsToList(subscriptions)
        .filter(isActiveSubscription)
        .filter(isAvailableSubscription);

  const plans: CreatorSubscriptionPlan[] =
    isLoadingCreatorSubscriptions && activeSubscriptions.length === 0
      ? []
      : [
          ...(followersSettings?.active !== false
            ? [
                {
                  level: PopStoreExtendedSubscriptionLevel.FOLLOWER,
                  name: 'Followers',
                  description:
                    followersSettings?.description ||
                    `Get Access to free content and updates from ${storeFrontName}.`,
                } satisfies CreatorSubscriptionPlan,
              ]
            : []),
          ...activeSubscriptions.map<CreatorSubscriptionPlan>(subscription => ({
            // A paid subscription's `platform_sub_level` (subscriber/fan/vip) is
            // exactly the paid subset of the extended enum — same string values.
            level: subscription.platform_sub_level as unknown as Exclude<
              CreatorSubscriptionPlan['level'],
              PopStoreExtendedSubscriptionLevel.FOLLOWER
            >,
            name: getSubscriptionDisplayName(
              subscription,
              subscription.platform_sub_level,
            ),
            description:
              subscription.subscription_data.creator_subscription
                .creator_sub_description,
            subscription,
          })),
        ];

  const isUpgrading = currentSubscriptionInfo?.activeSubscriptionLevel != null;

  // Preselect a sensible default once the plan list + user standing are known,
  // unless the user has already picked. Mirrors RN's default-plan effect.
  useEffect(() => {
    if (
      selectedPlan != null ||
      plans.length === 0 ||
      subscriptions == null ||
      isLoadingUserCurrentSubscription ||
      // Wait for follow status so we don't transiently default to Followers
      // before we know the viewer already follows.
      isFollowing === undefined
    ) {
      return;
    }

    let defaultPlan: CreatorSubscriptionPlan | undefined;
    if (minLevel != null) {
      defaultPlan = plans.find(x => x.level === minLevel);
    }

    if (defaultPlan == null) {
      let activeLevel = (currentSubscriptionInfo?.activeSubscriptionLevel ??
        undefined) as PopStoreExtendedSubscriptionLevel | undefined;
      // RN: a follow with no paid sub counts as the FOLLOWER current level, so
      // the default lands on the next tier up rather than back on Followers.
      if (activeLevel == null && isFollowing) {
        activeLevel = PopStoreExtendedSubscriptionLevel.FOLLOWER;
      }
      const currentIndex = plans.findIndex(x => x.level === activeLevel);
      defaultPlan = plans[currentIndex + 1] ?? plans[currentIndex];
    }

    if (
      defaultPlan == null ||
      compareExtendedSubscriptionLevel(defaultPlan.level, defaultLevel) < 0
    ) {
      defaultPlan =
        plans.find(x => x.level === defaultLevel) ?? defaultPlan ?? plans[0];
    }

    if (defaultPlan) {
      setSelectedPlan({
        plan: defaultPlan,
        discountForLongerCommitment: false,
      });
    }
  }, [
    defaultLevel,
    minLevel,
    plans,
    selectedPlan,
    subscriptions,
    isLoadingUserCurrentSubscription,
    currentSubscriptionInfo?.activeSubscriptionLevel,
    isFollowing,
  ]);

  // The tier list is "loading" until the creator's subscriptions have been
  // fetched at least once (`subscriptions == null`). This covers both the
  // auth-resolving window and the doomed token-less first fetch (which throws and
  // flips `isLoadingCreatorSubscriptions` back to false with no data), so the
  // picker shows skeletons throughout instead of briefly flashing the lone free
  // Follower row before the paid tiers arrive. A later refresh (subscriptions
  // already loaded → non-null) keeps the existing tiers visible, never skeletons.
  const loading = subscriptions == null || isLoadingUserCurrentSubscription;

  return {
    loading,
    plans,
    selectedPlan,
    setSelectedPlan,
    currentSubscriptionInfo,
    isUpgrading,
    minLevel,
  };
}

/**
 * Build the checkout URL for a paid tier. The picker navigates here (the
 * `community_membership_subscription` checkout owns payment + success). VIP with
 * the longer-commitment term selected passes `commitment: 'long'`.
 */
export function buildSubscribeCheckoutUrl(args: {
  /** The creator's public `url_code` (the seller-backed checkout target). */
  urlCode: string;
  selected: SelectedPlan;
  agreeToOffers: boolean;
  consentToSharePhone: boolean;
  /**
   * Optional post-success redirect (`redirect_to`). Set by the PDP subscription
   * gate so the buyer returns to the product after subscribing; omitted
   * elsewhere (checkout uses its default success destination).
   */
  successRedirectUrl?: string;
}): string {
  const {
    urlCode,
    selected,
    agreeToOffers,
    consentToSharePhone,
    successRedirectUrl,
  } = args;
  // Paid tiers only — FOLLOWER is handled inline via follow(), never here. The
  // paid extended levels share their string values with PopStoreSubscriptionLevel.
  const plan = selected.plan.level as unknown as PopStoreSubscriptionLevel;
  const builder = checkoutUrlBuilder().communitySubscription(b => {
    b.urlCode(urlCode)
      .plan(plan)
      .commitment(selected.discountForLongerCommitment ? 'long' : 'default')
      .agreeToOffers(agreeToOffers)
      .consentToSharePhone(consentToSharePhone);
  });
  if (successRedirectUrl) {
    builder.onSuccess(s => s.redirectTo(successRedirectUrl));
  }
  return builder.url();
}
